import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { logger } from "@/lib/logger";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [s, o, c, sub] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/contacts"),
        api.get("/admin/subscribers"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setContacts(c.data);
      setSubs(sub.data);
    } catch (e) {
      if (e.response?.status === 401) {
        nav("/admin/login");
      }
    }
  }, [nav, setStats, setOrders, setContacts, setSubs]);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/me")
      .then(() => {
        if (alive) refresh();
      })
      .catch(() => {
        if (alive) nav("/admin/login");
      });
    return () => {
      alive = false;
    };
  }, [refresh, nav]);

  const logout = async () => {
    try {
      await api.post("/admin/logout");
    } catch (e) {
      logger.error("Logout request failed:", e);
    }
    nav("/admin/login");
  };

  const updateOrder = async (num, patch) => {
    try {
      await api.put(`/admin/orders/${num}`, patch);
      toast.success("Order updated");
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="fade-in" data-testid="page-admin-dashboard">
      <SEO title="Admin dashboard" path="/admin" />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-16">
        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow">Admin</p>
            <h1 className="text-h2">Dashboard</h1>
          </div>
          <button onClick={logout} className="btn-secondary flex items-center gap-2" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { l: "Revenue", v: money(stats.revenue) },
              { l: "Paid orders", v: stats.paid_orders },
              { l: "Total orders", v: stats.total_orders },
              { l: "Contacts", v: stats.total_contacts },
              { l: "Subscribers", v: stats.total_subscribers },
            ].map((s) => (
              <div key={s.l} className="soft-card p-5">
                <p className="text-xs text-[#86868B]">{s.l}</p>
                <p className="font-display text-2xl font-bold mt-1" data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g, "-")}`}>{s.v}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="orders" className="mt-10">
          <TabsList>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="subs" data-testid="admin-tab-subs">Subscribers</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[#86868B] text-xs uppercase tracking-widest">
                  <tr>
                    <th className="py-3">Order</th>
                    <th>Email</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th>Fulfillment</th>
                    <th>Tracking</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  {orders.map((o) => (
                    <tr key={o.order_number} className="align-top">
                      <td className="py-3 font-mono-tech">{o.order_number}</td>
                      <td>{o.email}</td>
                      <td>{money(o.total)}</td>
                      <td><span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status === "paid" ? "bg-[#34C759]/10 text-[#34C759]" : "bg-[#F5F5F7]"}`}>{o.payment_status}</span></td>
                      <td>
                        <select
                          defaultValue={o.fulfillment_status}
                          onChange={(e) => updateOrder(o.order_number, { fulfillment_status: e.target.value })}
                          className="text-xs bg-[#F5F5F7] rounded-lg px-2 py-1"
                          data-testid={`order-fulfill-${o.order_number}`}
                        >
                          {["unfulfilled", "processing", "shipped", "delivered", "cancelled"].map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          defaultValue={o.tracking_number || ""}
                          placeholder="Tracking #"
                          onBlur={(e) => e.target.value !== (o.tracking_number || "") && updateOrder(o.order_number, { tracking_number: e.target.value })}
                          className="text-xs bg-[#F5F5F7] rounded-lg px-2 py-1 w-32"
                          data-testid={`order-track-${o.order_number}`}
                        />
                      </td>
                      <td className="text-xs text-[#86868B]">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && <p className="text-[#86868B] py-8 text-center">No orders yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6 space-y-3">
            {contacts.map((c) => (
              <div key={c.id} className="soft-card p-5">
                <div className="flex items-center justify-between text-sm">
                  <p className="font-semibold">{c.name} <span className="text-[#86868B]">&lt;{c.email}&gt;</span></p>
                  <p className="text-xs text-[#86868B]">{new Date(c.created_at).toLocaleString()}</p>
                </div>
                {c.subject && <p className="text-xs eyebrow mt-2">{c.subject}</p>}
                <p className="mt-2 text-sm text-[#1D1D1F]">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <p className="text-[#86868B] py-8 text-center">No messages yet.</p>}
          </TabsContent>

          <TabsContent value="subs" className="mt-6">
            <ul className="grid md:grid-cols-3 gap-2">
              {subs.map((s) => (
                <li key={s.id} className="text-sm bg-[#F5F5F7] rounded-xl px-4 py-2 flex justify-between">
                  <span>{s.email}</span>
                  <span className="text-xs text-[#86868B]">{new Date(s.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
            {subs.length === 0 && <p className="text-[#86868B] py-8 text-center">No subscribers yet.</p>}
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
